import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { dataService, NotificationGroup } from '../services/dataService';
import { ROUTES } from '../src/routes';
import { useDataRefresh } from '../src/hooks/useDataRefresh';
import { useSSEConnectionStatus } from '../src/hooks/useSSEConnectionStatus';
import { notify } from '../src/lib/notify';
import { playNotificationSound } from '../src/lib/notificationSound';
import NotificationGroupRow from './NotificationGroupRow';
import { cn } from '../src/ui/cn';
import { HEADER_MENU_PANEL } from '../src/ui/headerMenu';

const NotificationBell: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [groups, setGroups] = useState<NotificationGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);
  const sseConnected = useSSEConnectionStatus();

  useEffect(() => {
    dataService.getNotificationPreferences().then(p => setSoundEnabled(p.soundEnabled)).catch(() => {});
  }, []);

  const refreshUnreadCount = useCallback(() => {
    dataService.getUnreadNotificationCount().then(setUnreadCount).catch(() => {});
  }, []);

  const loadGroups = useCallback(() => {
    setIsLoading(true);
    setLoadError(null);
    dataService.getNotifications({ page: 1, pageSize: 20 })
      .then(data => setGroups(data.items))
      .catch(() => {
        setGroups([]);
        setLoadError(t('notifications.loadError'));
      })
      .finally(() => setIsLoading(false));
  }, [t]);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  useEffect(() => {
    if (isOpen) loadGroups();
  }, [isOpen, loadGroups]);

  // Cada notificación nueva (evento SSE real, no la Loads inicial) refresca el badge y, si el
  // dropdown está abierto, la lista; además dispara un toast transitorio con el título/mensaje
  // real del evento (antes mostraba un texto genérico vacío -- el payload SSE ahora los trae,
  // ver notifyClients en server.ts).
  useDataRefresh(['notification'], (detail) => {
    refreshUnreadCount();
    if (isOpen) loadGroups();
    else notify.info(detail?.titulo || t('notifications.title'), detail?.mensaje);
    if (soundEnabled) playNotificationSound();
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleGroupClick = (group: NotificationGroup) => {
    setIsOpen(false);
    dataService.markNotificationRead(group.latestId).catch(() => {});
    setUnreadCount(c => Math.max(0, c - group.unreadCount));
    if (group.link) navigate(group.link);
  };

  const handleArchiveToggle = (group: NotificationGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    const wasArchived = group.archivada;
    setGroups(prev => prev.filter(g => g.latestId !== group.latestId));
    const action = wasArchived ? dataService.unarchiveNotification : dataService.archiveNotification;
    action(group.latestId).catch(() => loadGroups());
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await dataService.markAllNotificationsRead();
      setUnreadCount(0);
      setGroups(prev => prev.map(g => ({ ...g, unreadCount: 0 })));
    } catch { /* best-effort */ }
  };

  const locale = i18n.resolvedLanguage || i18n.language;

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        // Lo unico que la campana decia en voz alta era el numero del globo rojo: un
        // lector de pantalla anunciaba «boton, 3». El nombre lleva ahora las dos cosas.
        aria-label={unreadCount > 0
          ? t('notifications.bellWithCount', { count: unreadCount })
          : t('notifications.title')}
        aria-expanded={isOpen}
        data-tooltip={sseConnected ? t('notifications.title') : t('notifications.connectionLost')}
        data-testid="notification-bell"
        className="relative p-2 sm:p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-brand-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer border border-slate-200 dark:border-slate-800"
      >
        <Bell size={18} />
        {!sseConnected && (
          <span
            data-testid="sse-disconnected-indicator"
            data-tooltip={t('notifications.connectionLost')}
            className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-slate-400 dark:bg-slate-500 border-2 border-white dark:border-slate-950 flex items-center justify-center"
          >
            <WifiOff size={8} className="text-white" />
          </span>
        )}
        {unreadCount > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-black leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div data-testid="notification-dropdown" className={cn(HEADER_MENU_PANEL, 'sm:w-80')}>
          <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 mb-1 flex items-center justify-between">
            <p className="label-field">{t('notifications.title')}</p>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="flex items-center gap-1 text-[10px] font-bold text-brand-600 hover:underline cursor-pointer"
              >
                <CheckCheck size={12} /> {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {!sseConnected && (
            <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 flex items-center gap-2">
              <WifiOff size={12} className="text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-[10px] font-bold text-amber-700 dark:text-amber-400">{t('notifications.connectionLost')}</p>
            </div>
          )}

          <div className="max-h-96 overflow-y-auto">
            {isLoading && (
              <p className="px-4 py-6 text-center text-xs font-medium text-slate-400">{t('notifications.loading')}</p>
            )}
            {!isLoading && loadError && (
              <p className="px-4 py-6 text-center text-xs font-medium text-red-500">{loadError}</p>
            )}
            {!isLoading && !loadError && groups.length === 0 && (
              <p className="px-4 py-6 text-center text-xs font-medium text-slate-400">{t('notifications.empty')}</p>
            )}
            {!isLoading && !loadError && groups.map(group => (
              <NotificationGroupRow
                key={`${group.entidad}::${group.entidad_id ?? ''}`}
                group={group}
                locale={locale}
                onClick={() => handleGroupClick(group)}
                onArchiveToggle={(e) => handleArchiveToggle(group, e)}
                compact
              />
            ))}
          </div>

          <div className="px-4 pt-2 mt-1 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => { setIsOpen(false); navigate(ROUTES.notifications); }}
              className="w-full text-center py-2 text-[10px] font-black text-brand-600 uppercase tracking-widest hover:underline cursor-pointer"
            >
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
